import { useState } from "react";
import SectionHeader from "@/components/feature/panel/SectionHeader";
import PanelLinks from "@/components/feature/panel/PanelLinks";
import { getDataClient } from "@/lib/dataClient";
import { examTypes, gradeOptions, subjectOptions, difficultyOptions } from "@/data/panels";

const inputClass =
  "w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none transition-colors";
const labelClass = "block text-sm font-medium text-foreground-700 mb-1.5";

const OPTION_LETTERS = ["A", "B", "C", "D", "E"];

export default function SoruEkle() {
  const [exam, setExam] = useState("");
  const [grade, setGrade] = useState("");
  const [subject, setSubject] = useState("");
  const [topic, setTopic] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [questionText, setQuestionText] = useState("");
  const [options, setOptions] = useState<string[]>(["", "", "", "", ""]);
  const [correctLetter, setCorrectLetter] = useState("");
  const [solution, setSolution] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const setOption = (idx: number, value: string) => {
    setOptions((prev) => prev.map((o, i) => (i === idx ? value : o)));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setMessage(null);

    const filledOptions = options.map((o) => o.trim());
    if (!subject.trim() || !topic.trim() || !questionText.trim() || !correctLetter) {
      setMessage({ type: "error", text: "Lütfen ders, konu, soru ve doğru cevabı doldurun." });
      return;
    }
    if (filledOptions.filter(Boolean).length < 2) {
      setMessage({ type: "error", text: "En az iki seçenek girin." });
      return;
    }
    const correctIndex = OPTION_LETTERS.indexOf(correctLetter);
    if (correctIndex < 0 || !filledOptions[correctIndex]) {
      setMessage({ type: "error", text: "Doğru cevap olarak seçtiğiniz seçenek boş." });
      return;
    }

    setSaving(true);
    try {
      const dataClient = getDataClient();
      const { error } = await dataClient.from("questions").insert({
        subject: subject.trim(),
        topic: topic.trim(),
        difficulty: difficulty || "Orta",
        qtype: "multiple_choice",
        question_text: questionText.trim(),
        options: filledOptions,
        correct_index: correctIndex,
        solution: solution.trim() || null,
      });
      if (error) throw error;

      setMessage({ type: "success", text: "Soru başarıyla kaydedildi." });
      setQuestionText("");
      setOptions(["", "", "", "", ""]);
      setCorrectLetter("");
      setSolution("");
      setTopic("");
    } catch (err) {
      setMessage({
        type: "error",
        text: err instanceof Error ? err.message : "Soru kaydedilirken bir hata oluştu.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <SectionHeader
        title="Soru Ekle"
        description="Soru bankasına yeni bir soru ekle. Kaydettiğin sorular anında kullanılabilir."
      />

      <form onSubmit={handleSubmit} className="rounded-lg border border-background-200 bg-background-50 p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Sınav</label>
            <select value={exam} onChange={(e) => setExam(e.target.value)} className={inputClass}>
              <option value="">Seçin...</option>
              {examTypes.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Sınıf</label>
            <select value={grade} onChange={(e) => setGrade(e.target.value)} className={inputClass}>
              <option value="">Seçin...</option>
              {gradeOptions.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Ders</label>
            <select value={subject} onChange={(e) => setSubject(e.target.value)} className={inputClass}>
              <option value="">Seçin...</option>
              {subjectOptions.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Konu</label>
            <input
              type="text"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              className={inputClass}
              placeholder="Örn: Türev, Paragrafta Anlam..."
            />
          </div>
          <div>
            <label className={labelClass}>Zorluk</label>
            <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)} className={inputClass}>
              <option value="">Seçin...</option>
              {difficultyOptions.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4">
          <label className={labelClass}>Soru</label>
          <textarea
            rows={4}
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            className={`${inputClass} resize-none`}
            placeholder="Soru metnini yazın."
          />
        </div>

        <div className="mt-4 space-y-2.5">
          <label className={labelClass}>Seçenekler</label>
          {OPTION_LETTERS.map((letter, idx) => (
            <div key={letter} className="flex items-center gap-3">
              <span className="w-7 h-7 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 font-semibold text-sm shrink-0">
                {letter}
              </span>
              <input
                type="text"
                value={options[idx]}
                onChange={(e) => setOption(idx, e.target.value)}
                className={inputClass}
                placeholder={`${letter} seçeneği`}
              />
            </div>
          ))}
        </div>

        <div className="mt-4">
          <label className={labelClass}>Doğru Cevap</label>
          <select
            value={correctLetter}
            onChange={(e) => setCorrectLetter(e.target.value)}
            className={`${inputClass} max-w-[180px]`}
          >
            <option value="">Seçin...</option>
            {OPTION_LETTERS.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </div>

        <div className="mt-4">
          <label className={labelClass}>Açıklamalı Çözüm</label>
          <textarea
            rows={3}
            value={solution}
            onChange={(e) => setSolution(e.target.value)}
            className={`${inputClass} resize-none`}
            placeholder="Çözüm açıklaması (isteğe bağlı)."
          />
        </div>

        {message && (
          <p
            className={`mt-4 text-sm rounded-md p-3 ${
              message.type === "success"
                ? "bg-primary-100 text-primary-800"
                : "bg-warm/15 text-foreground-800"
            }`}
          >
            {message.text}
          </p>
        )}

        <div className="mt-6 pt-5 border-t border-background-200">
          <button
            type="submit"
            disabled={saving}
            className="whitespace-nowrap px-5 py-2.5 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? "Kaydediliyor..." : "Soruyu Kaydet"}
          </button>
        </div>
      </form>

      <PanelLinks
        links={[
          { label: "Test Oluştur", href: "/ogretmen/test-olustur", icon: "ri-file-list-3-line" },
          { label: "Deneme Oluştur", href: "/ogretmen/deneme-olustur", icon: "ri-file-text-line" },
        ]}
      />
    </div>
  );
}